/**
 * UrgencyEngine — CRO conversion accelerator
 * Coordinated via NotificationCoordinator to avoid popup overload.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Clock, Zap, TrendingUp } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { requestSlot, releaseSlot } from '@/lib/notification-coordinator';

const SLOT_ID = 'urgency-engine';

const urgencyContent: Record<string, { visitors: string; offer: string; limited: string; closing: string }> = {
  en: { visitors: 'people viewing this page now', offer: 'Free first week — offer ends soon', limited: 'Only 3 slots left for March', closing: 'Closing fast' },
  fr: { visitors: 'personnes consultent cette page', offer: 'Première semaine gratuite — offre limitée', limited: 'Plus que 3 places pour mars', closing: 'Se remplit vite' },
  es: { visitors: 'personas viendo esta página', offer: 'Primera semana gratis — oferta limitada', limited: 'Solo 3 plazas para marzo', closing: 'Se llena rápido' },
  ar: { visitors: 'أشخاص يشاهدون هذه الصفحة', offer: 'الأسبوع الأول مجاناً — عرض محدود', limited: 'متبقي 3 أماكن فقط لشهر مارس', closing: 'يمتلئ بسرعة' },
  de: { visitors: 'Personen sehen diese Seite', offer: 'Erste Woche kostenlos — Angebot endet bald', limited: 'Nur noch 3 Plätze für März', closing: 'Schnell ausgebucht' },
  it: { visitors: 'persone stanno visualizzando', offer: 'Prima settimana gratis — offerta limitata', limited: 'Solo 3 posti per marzo', closing: 'Si riempie velocemente' },
  pt: { visitors: 'pessoas visualizando esta página', offer: 'Primeira semana grátis — oferta limitada', limited: 'Apenas 3 vagas para março', closing: 'Preenchendo rápido' },
  nl: { visitors: 'mensen bekijken deze pagina', offer: 'Eerste week gratis — beperkt aanbod', limited: 'Nog maar 3 plekken voor maart', closing: 'Snel vol' },
  pl: { visitors: 'osób ogląda tę stronę', offer: 'Pierwszy tydzień gratis — oferta ograniczona', limited: 'Tylko 3 miejsca na marzec', closing: 'Szybko się zapełnia' },
  tr: { visitors: 'kişi bu sayfayı görüntülüyor', offer: 'İlk hafta ücretsiz — sınırlı teklif', limited: 'Mart için sadece 3 yer kaldı', closing: 'Hızla doluyor' },
  ru: { visitors: 'человек просматривают страницу', offer: 'Первая неделя бесплатно — ограничено', limited: 'Осталось 3 места на март', closing: 'Быстро заполняется' },
  zh: { visitors: '人正在浏览此页面', offer: '首周免费 — 限时优惠', limited: '3月仅剩3个名额', closing: '即将满员' },
  ja: { visitors: '人がこのページを閲覧中', offer: '初週無料 — 期間限定', limited: '3月残り3枠', closing: 'まもなく満員' },
  ko: { visitors: '명이 이 페이지를 보고 있습니다', offer: '첫 주 무료 — 한정 혜택', limited: '3월 잔여 3석', closing: '빠르게 마감 중' },
  sv: { visitors: 'personer tittar på denna sida', offer: 'Första veckan gratis — begränsat erbjudande', limited: 'Bara 3 platser kvar i mars', closing: 'Fylls snabbt' },
  da: { visitors: 'personer ser denne side', offer: 'Første uge gratis — begrænset tilbud', limited: 'Kun 3 pladser tilbage i marts', closing: 'Fyldes hurtigt' },
};

export function UrgencyEngine() {
  const { language } = useLanguage();
  const [liveCount, setLiveCount] = useState(0);
  const [showBanner, setShowBanner] = useState(false);
  const [currentSignal, setCurrentSignal] = useState(0);

  const c = urgencyContent[language] || urgencyContent.en;

  useEffect(() => {
    const base = 12 + (new Date().getHours() % 8);
    setLiveCount(base + Math.floor(Math.random() * 6));
    const interval = setInterval(() => {
      setLiveCount(prev => prev + Math.floor(Math.random() * 3) - 1);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // Try to acquire a notification slot periodically
  useEffect(() => {
    const tryShow = () => {
      if (!showBanner && requestSlot(SLOT_ID)) {
        setShowBanner(true);
        // Auto-hide after 6 seconds and release slot
        setTimeout(() => {
          setShowBanner(false);
          releaseSlot(SLOT_ID);
        }, 6000);
      }
    };
    // First attempt after 12s, then every 45s
    const initial = setTimeout(tryShow, 12000);
    const interval = setInterval(tryShow, 45000);
    return () => { clearTimeout(initial); clearInterval(interval); };
  }, [showBanner]);

  // Rotate signals
  useEffect(() => {
    if (!showBanner) return;
    const interval = setInterval(() => {
      setCurrentSignal(prev => (prev + 1) % 3);
    }, 6000);
    return () => clearInterval(interval);
  }, [showBanner]);

  const signals = useMemo(() => [
    { icon: Users, text: `${Math.max(liveCount, 8)} ${c.visitors}`, color: 'text-cyan' },
    { icon: Clock, text: c.offer, color: 'text-orange' },
    { icon: Zap, text: c.limited, color: 'text-emerald-400' },
  ], [liveCount, c]);

  const Signal = signals[currentSignal];
  const IconComponent = Signal.icon;

  const handleDismiss = useCallback(() => {
    setShowBanner(false);
    releaseSlot(SLOT_ID);
  }, []);

  return (
    <AnimatePresence mode="wait">
      {showBanner && (
        <motion.div
          key={`urgency-${currentSignal}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed bottom-20 left-4 z-40 max-w-xs"
        >
          <div className="glass-card px-4 py-3 flex items-center gap-3 border border-white/10 shadow-xl">
            <div className={`p-2 rounded-full bg-white/5 ${Signal.color}`}>
              <IconComponent className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${Signal.color} truncate`}>{Signal.text}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> {c.closing}
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="text-muted-foreground hover:text-white text-xs p-1"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
